/**
 * Splits an iOOH crosstab column header into its label and its base (M4S-5626, BT-2055).
 *
 * A Studio workbook writes each column header as the label and, on the next line, the base:
 * "New York - Unexposed\nn=259". The exporter's market lookup matches the label with a "- Unexposed" /
 * "- Exposed" pattern anchored at the end, so the base line hid every market column of an uploaded
 * workbook from it. This is the one place that reads the two apart.
 *
 * Kept out of `iooh-crosstab-parser.ts`, which imports ExcelJS at load: the slide exporter and its tests
 * read headers without ever opening a workbook. The parser re-exports it, so both import paths work.
 * Pure, and imports nothing.
 */

/** A base at the very end of a header: `n=259`, `n = 1,204`, `(n=300)`, after any space or newline. */
const TRAILING_BASE = /\s*\(?\bn\s*=\s*([\d,]+)\)?\s*$/i;

/**
 * The label of a column header and the base it carries, or `null` when it carries none. A header
 * without an `n=` is not a base of 0: `parseBaseFromHeader` in the parser reads both as 0, which cannot
 * be told from a column with nobody in it.
 */
export function splitColumnHeader(header: string): { label: string; base: number | null } {
    const m = TRAILING_BASE.exec(header);
    const base = m ? parseInt(m[1].replace(/,/g, ''), 10) : NaN;
    if (!m || Number.isNaN(base)) return { label: header.trim(), base: null };
    return { label: header.slice(0, m.index).trim(), base };
}
